import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { Modal, Input, Button, Typography, theme } from 'antd';
import { AnimatePresence, motion, useDragControls } from 'framer-motion';
import { IconFlag } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import { message } from '../services/toastMessage';
import { useAppBreakpoint } from '../hooks/useAppBreakpoint';
import { createReport, ReportTargetType } from '../services/ReportService';
import { TRANSLATIONS } from '../constants/translations';
import { translate } from '../helpers/translationHelper';

const { TextArea } = Input;
const { Text } = Typography;

interface ReportModalProps {
  open: boolean;
  targetType: ReportTargetType;
  targetId: string;
  onClose: () => void;
}

/**
 * Shared "reportar" modal -- reused by every entry point (space, host
 * profile, chat message, photo) instead of a bespoke form per page, since
 * they all boil down to the same TargetType/TargetId/reason shape (see
 * ReportsController.CreateReport). Self-contained (owns its own submit/
 * toast/close), unlike AdminReasonModal, since every caller here wants the
 * exact same behavior on success/error -- there's no per-page follow-up
 * action the way an admin action's onConfirm often has one.
 *
 * Mobile gets the app's own creamy bottom-sheet language (same portal/
 * backdrop/drag-to-dismiss shape as HostCancelBookingModal, which this
 * mirrors) instead of a bare antd Modal with its default black "OK" button --
 * desktop keeps the plain Modal, same split every other reason-collecting
 * dialog in this app already uses.
 */
const ReportModal: React.FC<ReportModalProps> = ({ open, targetType, targetId, onClose }) => {
  const { t } = useTranslation();
  const { isMobile } = useAppBreakpoint();
  const { token } = theme.useToken();
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  // Drag only starts from the grab handle, not from anywhere on the sheet --
  // same reasoning as HostCancelBookingModal's own dragControls: this sheet
  // holds a TextArea the user needs to tap/scroll/select text inside, which
  // a whole-sheet drag listener would fight with.
  const dragControls = useDragControls();

  const handleClose = () => {
    setReason('');
    onClose();
  };

  const handleSubmit = async () => {
    if (!reason.trim()) return;
    setSubmitting(true);
    try {
      await createReport(targetType, targetId, reason.trim());
      message.success(translate(t, TRANSLATIONS.REPORT_SUCCESS));
      setReason('');
      onClose();
    } catch {
      message.error(translate(t, TRANSLATIONS.REPORT_ERROR));
    } finally {
      setSubmitting(false);
    }
  };

  if (isMobile) {
    return createPortal(
      <AnimatePresence>
        {open && (
          <>
            <motion.div
              key="report-sheet-backdrop"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              onClick={handleClose}
              style={{ position: 'fixed', inset: 0, zIndex: 2100, background: 'rgba(0,0,0,0.45)' }}
            />
            <motion.div
              key="report-sheet"
              drag="y"
              dragControls={dragControls}
              dragListener={false}
              dragConstraints={{ top: 0, bottom: 0 }}
              dragElastic={{ top: 0, bottom: 0.5 }}
              dragSnapToOrigin
              onDragEnd={(_, info) => {
                if (info.offset.y > 80 || info.velocity.y > 500) handleClose();
              }}
              initial={{ y: '100%' }}
              animate={{ y: 0 }}
              exit={{ y: '100%' }}
              transition={{ duration: 0.25, ease: 'easeInOut' }}
              style={{
                position: 'fixed',
                left: 0,
                right: 0,
                bottom: 0,
                zIndex: 2101,
                width: '100%',
                maxWidth: 480,
                margin: '0 auto',
                background: token.colorBgContainer,
                borderRadius: '20px 20px 0 0',
                boxShadow: '0 -8px 24px rgba(0,0,0,0.16)',
                padding: '10px 20px calc(20px + env(safe-area-inset-bottom, 0px))',
              }}
            >
              <div
                onPointerDown={(e) => dragControls.start(e)}
                style={{
                  width: 36,
                  height: 4,
                  borderRadius: 2,
                  background: token.colorBorder,
                  margin: '0 auto 16px',
                  cursor: 'grab',
                  touchAction: 'none',
                }}
              />
              <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start', marginBottom: 14 }}>
                <span
                  style={{
                    display: 'flex',
                    flexShrink: 0,
                    width: 40,
                    height: 40,
                    borderRadius: '50%',
                    alignItems: 'center',
                    justifyContent: 'center',
                    background: token.colorWarningBg,
                    color: token.colorWarning,
                  }}
                >
                  <IconFlag size={20} />
                </span>
                <div style={{ minWidth: 0 }}>
                  <Text strong style={{ display: 'block', fontSize: 16 }}>
                    {translate(t, TRANSLATIONS.REPORT_MODAL_TITLE)}
                  </Text>
                </div>
              </div>
              <TextArea
                rows={4}
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder={translate(t, TRANSLATIONS.REPORT_REASON_PLACEHOLDER)}
                maxLength={1000}
                style={{ borderRadius: 16, marginBottom: 16 }}
              />
              <div style={{ display: 'flex', gap: 10 }}>
                <Button
                  block
                  style={{ borderRadius: 999, border: `1px solid ${token.colorBorder}`, background: 'transparent' }}
                  onClick={handleClose}
                >
                  {translate(t, TRANSLATIONS.REPORT_CANCEL_BUTTON)}
                </Button>
                <Button
                  type="primary"
                  block
                  loading={submitting}
                  disabled={!reason.trim()}
                  onClick={handleSubmit}
                  style={{ borderRadius: 999 }}
                >
                  {translate(t, TRANSLATIONS.REPORT_SUBMIT_BUTTON)}
                </Button>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>,
      document.body
    );
  }

  return (
    <Modal
      title={translate(t, TRANSLATIONS.REPORT_MODAL_TITLE)}
      open={open}
      onCancel={handleClose}
      onOk={handleSubmit}
      okText={translate(t, TRANSLATIONS.REPORT_SUBMIT_BUTTON)}
      okButtonProps={{ disabled: !reason.trim() }}
      confirmLoading={submitting}
    >
      <TextArea
        rows={3}
        value={reason}
        onChange={(e) => setReason(e.target.value)}
        placeholder={translate(t, TRANSLATIONS.REPORT_REASON_PLACEHOLDER)}
        maxLength={1000}
      />
    </Modal>
  );
};

export default ReportModal;
